import React, { useState } from 'react';
import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import SPAInput from '@/components/admin/SysadminSPA/shared/SPAInput';
import cn from '@/utils/cn';
import type { TestResult } from './types';

interface Props {
  isTesting: boolean;
  onTest: (variables: Record<string, string>) => void;
  testResult: TestResult | null;
  variables: string[];
}

const labelClass = 'tw-mb-1 tw-block tw-text-xs tw-text-spa-secondary';

const eyebrowClass = 'tw-m-0 tw-mb-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-spa-tertiary';

const codeBlockClass = cn(
  'tw-m-0 tw-whitespace-pre-wrap tw-break-words tw-rounded-md tw-border tw-border-solid tw-border-spa-border-secondary',
  'tw-bg-spa-bg-code tw-px-4 tw-py-3 tw-font-mono tw-text-[13px] tw-leading-normal tw-text-spa-primary',
);

const inlineCodeClass = cn(
  'tw-rounded tw-border tw-border-solid tw-border-spa-border-input tw-bg-spa-bg-tertiary',
  'tw-px-1.5 tw-py-0.5 tw-font-mono tw-text-spa-accent-text-hover',
);

const PromptTester = ({ isTesting, onTest, testResult, variables }: Props) => {
  const [values, setValues] = useState<Record<string, string>>(
    () => Object.fromEntries(variables.map(v => [v, ''])),
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onTest(values);
  };

  // React 16 pools synthetic events, so read the value before the state updater runs.
  const setValue = (variable: string, value: string) => {
    setValues(prev => ({ ...prev, [variable]: value }));
  };

  return (
    <section className="tw-mt-8 tw-border-0 tw-border-t tw-border-solid tw-border-spa-border-secondary tw-pt-6">
      <h3 className="tw-m-0 tw-mb-3 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wider tw-text-spa-primary">
        Test prompt
      </h3>
      <form className="tw-flex tw-flex-wrap tw-items-end tw-gap-x-4 tw-gap-y-3" onSubmit={handleSubmit}>
        {variables.map(variable => (
          <div key={variable} className="tw-w-56">
            <label className={labelClass} htmlFor={`var-${variable}`}>
              {`{{${variable}}}`}
            </label>
            <SPAInput
              id={`var-${variable}`}
              placeholder={variable}
              value={values[variable] || ''}
              onChange={e => setValue(variable, e.target.value)}
            />
          </div>
        ))}
        <SPAButton disabled={isTesting} type="submit" variant="primary">
          {isTesting ? 'Running…' : 'Run test'}
        </SPAButton>
      </form>

      {testResult?.output && (
        <div className="tw-mt-5">
          <p className={eyebrowClass}>Output</p>
          <pre className={codeBlockClass}>{testResult.output}</pre>
          {testResult.trace_id && (
            <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-spa-tertiary">
              Trace ID
              {' '}
              <code className={inlineCodeClass}>{testResult.trace_id}</code>
            </p>
          )}
        </div>
      )}

      {testResult?.error && (
        <p className="tw-mb-0 tw-mt-4 tw-text-sm tw-text-spa-error">{testResult.error}</p>
      )}
    </section>
  );
};

export default PromptTester;
